import { utilities as csUtils, cache } from '@cornerstonejs/core';
import type { Types } from '@cornerstonejs/core';
import type { VoxelManager } from '@cornerstonejs/core/utilities';
import IslandRemoval from '../floodFillIslandRemoval';
import { commitSliceMasksToLabelmapVolume } from '../commitSliceMasksToLabelmap';
import { createEnsureSliceLoadedForVolume } from '../createEnsureSliceLoadedForVolume';
import { floodFill3dSliceLazy } from '../floodFillSliceLazy';
import {
  DEFAULT_NEIGHBORHOOD_RADIUS,
  DEFAULT_POSITIVE_STD_DEV_MULTIPLIER,
} from './constants';
import { calculateNeighborhoodStatsVM } from './neighborhoodStats';
import { getViewportVoiMappingForVolume } from './getViewportVoiMappingForVolume';
import type {
  FloodFillIntensityRangeResult,
  FloodFillIntensityRangeOptions,
  GetFloodFillIntensityRange,
} from './floodFillIntensityRangeTypes';
const {
  transformWorldToIndex,
  mapScalarToViewportVoiIntensity,
  mapMappedBandToRawRange,
} = csUtils;

type NumberVoxelManager = VoxelManager<number>;
const { growCutLog: log } = csUtils.logger;
const ENABLE_VERBOSE_FLOOD_FILL_LOGS = false;

/** Timing helpers gated behind the verbose flag. */
const timingStarts = new Map<string, number>();
const timeStart = (label: string) => {
  if (ENABLE_VERBOSE_FLOOD_FILL_LOGS) {
    timingStarts.set(label, performance.now());
  }
};
const timeEnd = (label: string) => {
  if (ENABLE_VERBOSE_FLOOD_FILL_LOGS) {
    const start = timingStarts.get(label);
    if (start !== undefined) {
      log.debug(`${label}: ${performance.now() - start}ms`);
      timingStarts.delete(label);
    }
  }
};

export type {
  FloodFillIntensityRangeResult,
  FloodFillIntensityRangeOptions,
  GetFloodFillIntensityRange,
} from './floodFillIntensityRangeTypes';

/** Ref-volume metadata + labelmap dimension check. */
const FLOOD_FILL_PREP_TIMING_LABEL = 'cornerstone.tools: floodFill: prep';
const FLOOD_FILL_PREP_REF_META =
  'cornerstone.tools: floodFill: prep: ref_volume_meta';
const FLOOD_FILL_RANGE_TIMING_LABEL =
  'cornerstone.tools: floodFill: intensityRange';
const FLOOD_FILL_RUN_TIMING_LABEL =
  'cornerstone.tools: floodFill: fillAndIslandRemoval';
const FLOOD_FILL_ISLAND_EXTERNAL_TIMING_LABEL =
  'cornerstone.tools: floodFill: islandRemoval:external';
const FLOOD_FILL_ISLAND_INTERNAL_TIMING_LABEL =
  'cornerstone.tools: floodFill: islandRemoval:internal';

type FloodFillSegmentationOptions = {
  /** Cooperative cancellation hook; returns true to stop at next checkpoint. */
  isCancelled?: () => boolean;
  positiveStdDevMultiplier?: number;
  initialNeighborhoodRadius?: number;
  segmentIndex?: number;
  /**
   * Segment value written during the async flood (e.g. 255) so the active labelmap
   * can update slice-by-slice. After island removal, those voxels are promoted to
   * {@link segmentIndex}. Omit to use 255 when `segmentIndex !== 255`, or set equal
   * to `segmentIndex` to disable preview and paint the final index immediately.
   */
  floodPreviewSegmentIndex?: number;
  maxInternalRemove?: number;
  /**
   * When true (default), run planar island flood from the click then clear voxels
   * not marked ISLAND. Set false to keep the raw 3D flood fill result.
   */
  applyExternalIslandRemoval?: boolean;
  /**
   * When true (default), run internal hole / speckle cleanup after external removal.
   * Ignored if applyExternalIslandRemoval is false (nothing to clean in-labelmap).
   */
  applyInternalIslandRemoval?: boolean;
  /** Forwarded to IslandRemoval: logs bounds, per-click flood, and voxel counts. */
  islandRemovalVerboseLogging?: boolean;
  getIntensityRange?: GetFloodFillIntensityRange;
  /** Viewport container; passed into `getIntensityRange` / canvas-disk strategy. */
  element?: HTMLDivElement;
  /** Pointer position in viewport canvas coordinates (same space as `canvasDiskRadiusPx`). */
  canvasPoint?: { x: number; y: number };
  /** Circular sampling ROI radius in canvas pixels (see tool `intensitySamplingDiskRadiusCanvasPx`). */
  intensitySamplingDiskRadiusCanvasPx?: number;
  /**
   * 3D: load slice images on demand (streaming volumes). Built from volume when omitted.
   */
  ensureSliceLoaded?: (sliceIndex: number) => Promise<void>;
  yieldEvery?: number;
  /**
   * When true, forwarded to {@link floodFill} as `planar` (same acquisition slice as seed, fixed k).
   */
  planar?: boolean;
  /** Optional flood bound in k from seed: keep voxels with `abs(k-seedK) <= maxDeltaK`. */
  maxDeltaK?: number;
  /**
   * Optional flood bound in i/j from seed: keep voxels with
   * `abs(i-seedI) <= maxDeltaIJ` and `abs(j-seedJ) <= maxDeltaIJ`.
   */
  maxDeltaIJ?: number;
  /**
   * Called with the labelmap ijk points the fill painted (before island
   * removal). Lets callers track exactly which voxels a run touched — e.g.
   * to clear and refill on expand/shrink.
   */
  onCommitted?: (floodedPoints: Types.Point3[]) => void;
  /**
   * Compute-safety budget on filled voxels. When the flood exceeds it,
   * NOTHING is committed to the labelmap, `onRejected` fires and the run
   * returns null.
   */
  maxVoxels?: number;
  /**
   * Periodic region-shape gate forwarded to the flood: returning false stops
   * the fill and rejects it (nothing committed). Lets tools require the
   * region to stay entity-like (compact, self-contained) as it grows.
   */
  shouldContinueRegion?: (stats: {
    voxelCount: number;
    bbox: { min: Types.Point3; max: Types.Point3 };
  }) => boolean;
  /** Fired when the fill was rejected by the budget or the shape gate. */
  onRejected?: (info: {
    voxelCount: number;
    bbox: { min: Types.Point3; max: Types.Point3 } | null;
  }) => void;
  /**
   * RLE history voxel manager wrapping the labelmap's voxel manager (e.g. a
   * `LabelmapMemo.voxelManager`). When provided, EVERY labelmap write — the
   * flood commit, preview promotion and island removal — goes through it so
   * the whole run is recorded as one undoable memo. It also acts as the
   * preview/delta layer for external island removal. Reads still use the
   * labelmap's own voxel manager (the history wrapper reads its delta map,
   * not current state).
   */
  historyVoxelManager?: Types.IVoxelManager<number>;
};

function getDisplayVoiSnapshot(
  viewport: Types.IViewport,
  referencedVolumeId?: string
): {
  lower: number;
  upper: number;
  windowWidth: number;
  windowCenter: number;
} | null {
  const getProps = (
    viewport as Types.IViewport & {
      getProperties?: (volumeId?: string) => {
        voiRange?: { lower: number; upper: number };
      };
    }
  ).getProperties;
  if (typeof getProps !== 'function') {
    return null;
  }
  const props = referencedVolumeId
    ? getProps.call(viewport, referencedVolumeId)
    : getProps.call(viewport);
  const voiRange = props?.voiRange;
  if (
    !voiRange ||
    typeof voiRange.lower !== 'number' ||
    typeof voiRange.upper !== 'number'
  ) {
    return null;
  }
  const { lower, upper } = voiRange;
  return {
    lower,
    upper,
    windowWidth: upper - lower,
    windowCenter: (lower + upper) / 2,
  };
}

/**
 * Raw neighborhood mean ± kσ (no VOI mapping).
 */
function getPositiveIntensityRangeRaw(
  referencedVolume: Types.IImageVolume,
  worldPosition: Types.Point3,
  options?: FloodFillIntensityRangeOptions
): FloodFillIntensityRangeResult | null {
  const { dimensions, imageData: refImageData } = referencedVolume;
  const [width, height, numSlices] = dimensions;
  const referenceVolumeVoxelManager =
    referencedVolume.voxelManager as unknown as NumberVoxelManager;

  const neighborhoodRadius =
    options?.initialNeighborhoodRadius ?? DEFAULT_NEIGHBORHOOD_RADIUS;
  const positiveK =
    options?.positiveStdDevMultiplier ?? DEFAULT_POSITIVE_STD_DEV_MULTIPLIER;

  const ijkStart = transformWorldToIndex(refImageData, worldPosition).map(
    Math.round
  ) as Types.Point3;

  if (
    ijkStart[0] < 0 ||
    ijkStart[0] >= width ||
    ijkStart[1] < 0 ||
    ijkStart[1] >= height ||
    ijkStart[2] < 0 ||
    ijkStart[2] >= numSlices
  ) {
    log.warn('intensity range: click outside volume', {
      ijkStart,
      dimensions: [width, height, numSlices],
    });
    return null;
  }

  const initialStats = calculateNeighborhoodStatsVM(
    referenceVolumeVoxelManager,
    dimensions,
    ijkStart,
    neighborhoodRadius
  );

  if (initialStats.count === 0) {
    const seedScalar = Number(
      referenceVolumeVoxelManager.getAtIJKPoint(ijkStart)
    );
    initialStats.mean = seedScalar;
    initialStats.stdDev = 0;
  }

  const min = initialStats.mean - positiveK * initialStats.stdDev;
  const max = initialStats.mean + positiveK * initialStats.stdDev;
  const startValue = referenceVolumeVoxelManager.getAtIJKPoint(ijkStart);

  if (startValue < min || startValue > max) {
    log.warn(
      'intensity range: clicked voxel intensity is outside the calculated positive range'
    );
    return null;
  }

  return {
    min,
    max,
    ijkStart,
    diagnostics: {
      neighborhoodMean: initialStats.mean,
      neighborhoodStdDev: initialStats.stdDev,
      clickedVoxelValue: startValue,
      positiveStdDevMultiplier: positiveK,
      neighborhoodRadius,
      strategy: 'meanStdRaw',
    },
  };
}

/**
 * Neighborhood mean ± kσ in VOI-mapped [0,1] space, band inverted to raw bounds.
 */
function getPositiveIntensityRangeVoiMapped(
  referencedVolume: Types.IImageVolume,
  worldPosition: Types.Point3,
  voiMapping: NonNullable<FloodFillIntensityRangeOptions['voiMapping']>,
  options?: FloodFillIntensityRangeOptions
): FloodFillIntensityRangeResult | null {
  const { dimensions, imageData: refImageData } = referencedVolume;
  const [width, height, numSlices] = dimensions;
  const referenceVolumeVoxelManager =
    referencedVolume.voxelManager as unknown as NumberVoxelManager;

  const neighborhoodRadius =
    options?.initialNeighborhoodRadius ?? DEFAULT_NEIGHBORHOOD_RADIUS;
  const positiveK =
    options?.positiveStdDevMultiplier ?? DEFAULT_POSITIVE_STD_DEV_MULTIPLIER;

  const ijkStart = transformWorldToIndex(refImageData, worldPosition).map(
    Math.round
  ) as Types.Point3;

  if (
    ijkStart[0] < 0 ||
    ijkStart[0] >= width ||
    ijkStart[1] < 0 ||
    ijkStart[1] >= height ||
    ijkStart[2] < 0 ||
    ijkStart[2] >= numSlices
  ) {
    log.info('intensity range (VOI-mapped): click outside volume', {
      ijkStart,
      dimensions: [width, height, numSlices],
    });
    return null;
  }

  const mapFn = (v: number) => mapScalarToViewportVoiIntensity(v, voiMapping);

  const initialStats = calculateNeighborhoodStatsVM(
    referenceVolumeVoxelManager,
    dimensions,
    ijkStart,
    neighborhoodRadius,
    mapFn
  );

  if (initialStats.count === 0) {
    const seedScalar = Number(
      referenceVolumeVoxelManager.getAtIJKPoint(ijkStart)
    );
    initialStats.mean = mapFn(seedScalar);
    initialStats.stdDev = 0;
  }

  let yMin = initialStats.mean - positiveK * initialStats.stdDev;
  let yMax = initialStats.mean + positiveK * initialStats.stdDev;
  yMin = Math.max(0, Math.min(1, yMin));
  yMax = Math.max(0, Math.min(1, yMax));
  if (yMin > yMax) {
    const t = yMin;
    yMin = yMax;
    yMax = t;
  }

  const { rawMin, rawMax } = mapMappedBandToRawRange(yMin, yMax, voiMapping);
  const startValue = Number(
    referenceVolumeVoxelManager.getAtIJKPoint(ijkStart)
  );
  const bandLo = Math.min(rawMin, rawMax);
  const bandHi = Math.max(rawMin, rawMax);
  const min = Math.min(bandLo, startValue);
  const max = Math.max(bandHi, startValue);

  return {
    min,
    max,
    ijkStart,
    diagnostics: {
      neighborhoodMean: initialStats.mean,
      neighborhoodStdDev: initialStats.stdDev,
      clickedVoxelValue: startValue,
      positiveStdDevMultiplier: positiveK,
      neighborhoodRadius,
      strategy: 'meanStdVoiMapped',
      mappedBand: { min: yMin, max: yMax },
    },
  };
}

function getPositiveIntensityRange(
  referencedVolume: Types.IImageVolume,
  worldPosition: Types.Point3,
  options?: FloodFillIntensityRangeOptions
): FloodFillIntensityRangeResult | null {
  if (options?.voiMapping) {
    return getPositiveIntensityRangeVoiMapped(
      referencedVolume,
      worldPosition,
      options.voiMapping,
      options
    );
  }
  return getPositiveIntensityRangeRaw(referencedVolume, worldPosition, options);
}

function resolveIntensityRange(
  referencedVolume: Types.IImageVolume,
  worldPosition: Types.Point3,
  viewport: Types.IViewport,
  referencedVolumeId: string,
  options: FloodFillSegmentationOptions,
  rangeContext: FloodFillIntensityRangeOptions
): FloodFillIntensityRangeResult | null {
  if (options.getIntensityRange) {
    const fromGetter = options.getIntensityRange(
      referencedVolume,
      worldPosition,
      rangeContext
    );
    if (!fromGetter) {
      log.warn(
        'flood fill intensity range: strategy/custom getIntensityRange returned null (e.g. missing canvasPoint/VOI for canvas-disk, click outside volume, or fixed-% rejection)',
        { referencedVolumeId, worldPosition }
      );
    }
    return fromGetter;
  }
  const voiFromViewport = getViewportVoiMappingForVolume(
    viewport,
    referencedVolumeId
  );
  const merged: FloodFillIntensityRangeOptions = {
    ...rangeContext,
    voiMapping: voiFromViewport ?? undefined,
  };
  let result = getPositiveIntensityRange(
    referencedVolume,
    worldPosition,
    merged
  );
  if (!result && merged.voiMapping) {
    log.info(
      'flood fill intensity range: VOI-mapped mean±σ failed; falling back to raw mean±σ',
      { referencedVolumeId }
    );
    result = getPositiveIntensityRange(referencedVolume, worldPosition, {
      ...merged,
      voiMapping: undefined,
    });
  }
  if (!result) {
    log.warn(
      'flood fill intensity range: default mean±σ (VOI + raw fallback) could not resolve a band',
      { referencedVolumeId, worldPosition }
    );
  }
  return result;
}

function assertFloodFillLabelmapMatchesRef(
  referencedVolume: Types.IImageVolume,
  labelmapVolume: Types.IImageVolume
) {
  const [rw, rh, rd] = referencedVolume.dimensions;
  const [lw, lh, ld] = labelmapVolume.dimensions;
  if (rw !== lw || rh !== lh || rd !== ld) {
    throw new Error(
      `runFloodFillSegmentation: labelmap dimensions [${lw},${lh},${ld}] must match referenced volume [${rw},${rh},${rd}]`
    );
  }
}

function resolveFloodPaintIndices(
  segmentIndex: number,
  explicitPreview?: number
): { paintIndex: number; usePreview: boolean } {
  if (explicitPreview !== undefined) {
    return {
      paintIndex: explicitPreview,
      usePreview: explicitPreview !== segmentIndex,
    };
  }
  if (segmentIndex === 255) {
    return { paintIndex: 255, usePreview: false };
  }
  return { paintIndex: 255, usePreview: true };
}

function promotePreviewSegmentToFinal(
  readVoxelManager: NumberVoxelManager,
  writeVoxelManager: NumberVoxelManager,
  preview: number,
  final: number,
  points: Types.Point3[],
  numPixelsPerSlice: number,
  width: number
) {
  for (let i = 0; i < points.length; i++) {
    const [x, y, z] = points[i];
    const index = z * numPixelsPerSlice + y * width + x;
    if (readVoxelManager.getAtIndex(index) === preview) {
      writeVoxelManager.setAtIndex(index, final);
    }
  }
}

async function runFloodFillSegmentation({
  referencedVolumeId,
  worldPosition,
  viewport,
  labelmapVolume,
  options = {},
}: {
  referencedVolumeId: string;
  worldPosition: Types.Point3;
  viewport: Types.IViewport;
  /** Active segmentation labelmap (same grid as referenced volume). */
  labelmapVolume: Types.IImageVolume;
  options?: FloodFillSegmentationOptions;
}): Promise<Types.IImageVolume | null> {
  timeStart(FLOOD_FILL_PREP_TIMING_LABEL);

  const referencedVolume = cache.getVolume(referencedVolumeId);
  assertFloodFillLabelmapMatchesRef(referencedVolume, labelmapVolume);
  const labelmap = labelmapVolume;

  const segmentIndex = options.segmentIndex ?? 1;
  const { paintIndex, usePreview } = resolveFloodPaintIndices(
    segmentIndex,
    options.floodPreviewSegmentIndex
  );

  timeStart(FLOOD_FILL_PREP_REF_META);
  const [volMin, volMax] = referencedVolume.voxelManager.getRange();
  const displayVoi = getDisplayVoiSnapshot(viewport, referencedVolumeId);
  log.info('segmentation path: flood fill (floodfill_full)', {
    referencedVolumeId,
    volumeScalarRange: { min: volMin, max: volMax },
    displayVoi,
    floodPreview: usePreview ? paintIndex : null,
    segmentIndex,
  });
  timeEnd(FLOOD_FILL_PREP_REF_META);

  timeEnd(FLOOD_FILL_PREP_TIMING_LABEL);

  const voiMapping = getViewportVoiMappingForVolume(
    viewport,
    referencedVolumeId
  );

  const rangeContext: FloodFillIntensityRangeOptions = {
    positiveStdDevMultiplier: options.positiveStdDevMultiplier,
    initialNeighborhoodRadius: options.initialNeighborhoodRadius,
    viewport,
    element: options.element,
    referencedVolumeId,
    canvasPoint: options.canvasPoint,
    canvasDiskRadiusPx: options.intensitySamplingDiskRadiusCanvasPx,
    voiMapping: voiMapping ?? undefined,
  };

  timeStart(FLOOD_FILL_RANGE_TIMING_LABEL);
  const rangeResult = resolveIntensityRange(
    referencedVolume,
    worldPosition,
    viewport,
    referencedVolumeId,
    options,
    rangeContext
  );
  timeEnd(FLOOD_FILL_RANGE_TIMING_LABEL);

  if (!rangeResult) {
    log.warn('flood fill: aborted before fill (no intensity range)', {
      referencedVolumeId,
      worldPosition,
    });
    return null;
  }

  const { min: rangeMin, max: rangeMax, ijkStart, diagnostics } = rangeResult;
  let clampedRangeMin = rangeMin;
  let clampedRangeMax = rangeMax;
  const MAX_WL_BAND_FRACTION = 0.2;
  if (voiMapping && diagnostics?.mappedBand) {
    const mapped = diagnostics.mappedBand;
    const mappedLo = Math.max(0, Math.min(1, Math.min(mapped.min, mapped.max)));
    const mappedHi = Math.max(0, Math.min(1, Math.max(mapped.min, mapped.max)));
    const mappedWidth = mappedHi - mappedLo;
    if (mappedWidth > MAX_WL_BAND_FRACTION) {
      const seedScalarPreClamp = Number(
        (
          referencedVolume.voxelManager as unknown as NumberVoxelManager
        ).getAtIJKPoint(ijkStart)
      );
      const seedMapped = Math.max(
        0,
        Math.min(
          1,
          mapScalarToViewportVoiIntensity(seedScalarPreClamp, voiMapping)
        )
      );
      const half = MAX_WL_BAND_FRACTION / 2;
      const clippedMappedLo = Math.max(0, seedMapped - half);
      const clippedMappedHi = Math.min(1, seedMapped + half);
      const { rawMin: clipRawMin, rawMax: clipRawMax } =
        mapMappedBandToRawRange(clippedMappedLo, clippedMappedHi, voiMapping);
      clampedRangeMin = Math.min(clipRawMin, clipRawMax, seedScalarPreClamp);
      clampedRangeMax = Math.max(clipRawMin, clipRawMax, seedScalarPreClamp);
      log.info('flood fill: clipped wide WL band around seed', {
        maxWlBandFraction: MAX_WL_BAND_FRACTION,
        originalMappedBand: {
          min: mappedLo,
          max: mappedHi,
          width: mappedWidth,
        },
        clippedMappedBand: {
          min: clippedMappedLo,
          max: clippedMappedHi,
          width: clippedMappedHi - clippedMappedLo,
        },
      });
    }
  }

  log.info('intensity tolerance band', {
    toleranceMin: clampedRangeMin,
    toleranceMax: clampedRangeMax,
    width: clampedRangeMax - clampedRangeMin,
    ...diagnostics,
  });
  if (ENABLE_VERBOSE_FLOOD_FILL_LOGS) {
    log.info('[cornerstone-tools] flood fill intensity range', {
      rawMin: clampedRangeMin,
      rawMax: clampedRangeMax,
      strategy: diagnostics.strategy,
      mappedBand: diagnostics.mappedBand,
      neighborhoodRadius: diagnostics.neighborhoodRadius,
    });
  }

  timeStart(FLOOD_FILL_RUN_TIMING_LABEL);
  try {
    const { dimensions } = referencedVolume;
    const [width, height, numSlices] = dimensions;
    const refVoxelManager =
      referencedVolume.voxelManager as unknown as NumberVoxelManager;
    const numPixelsPerSlice = width * height;

    // Reads always come from the labelmap itself; writes go through the
    // history wrapper when the caller records this run as an undoable memo.
    const labelmapReadVm =
      labelmap.voxelManager as unknown as NumberVoxelManager;
    const labelmapWriteVm = (options.historyVoxelManager ??
      labelmap.voxelManager) as unknown as NumberVoxelManager;

    let positiveMin = clampedRangeMin;
    let positiveMax = clampedRangeMax;
    const seedScalar = Number(refVoxelManager.getAtIJKPoint(ijkStart));
    if (Number.isFinite(seedScalar)) {
      if (seedScalar < positiveMin) {
        log.info('flood fill: expanded tolerance min to include seed voxel', {
          seedScalar,
          previousMin: positiveMin,
        });
        positiveMin = seedScalar;
      }
      if (seedScalar > positiveMax) {
        log.info('flood fill: expanded tolerance max to include seed voxel', {
          seedScalar,
          previousMax: positiveMax,
        });
        positiveMax = seedScalar;
      }
    }
    if (ENABLE_VERBOSE_FLOOD_FILL_LOGS) {
      log.info('[cornerstone-tools] flood fill seed + effective tolerance', {
        seedScalar,
        effectiveMin: positiveMin,
        effectiveMax: positiveMax,
      });
    }

    const intensityGetter = (
      x: number,
      y: number,
      z: number
    ): number | undefined => {
      if (
        x < 0 ||
        x >= width ||
        y < 0 ||
        y >= height ||
        z < 0 ||
        z >= numSlices
      ) {
        return undefined;
      }
      const idx = z * numPixelsPerSlice + y * width + x;
      return refVoxelManager.getAtIndex(idx);
    };

    const inRange = (val: number) => val >= positiveMin && val <= positiveMax;

    const ensureSliceLoaded =
      options.ensureSliceLoaded ??
      createEnsureSliceLoadedForVolume(referencedVolume);

    const planar = options.planar === true;

    if (planar) {
      log.info('flood fill: planar mode (fixed slice index k)', { ijkStart });
    }

    const {
      sliceMasks,
      voxelCount: filledVoxelCount,
      truncated,
      bbox,
    } = await floodFill3dSliceLazy(intensityGetter, ijkStart, {
      width,
      height,
      depth: numSlices,
      equals: (val, _startVal) =>
        val !== undefined && typeof val === 'number' && inRange(val),
      ensureSliceLoaded,
      yieldEvery: options.yieldEvery ?? 500,
      planar,
      maxDeltaK: options.maxDeltaK,
      maxDeltaIJ: options.maxDeltaIJ,
      isCancelled: options.isCancelled,
      maxVoxels: options.maxVoxels,
      shouldContinue: options.shouldContinueRegion,
    });

    const finalShapeRejected =
      !truncated &&
      bbox &&
      options.shouldContinueRegion &&
      !options.shouldContinueRegion({ voxelCount: filledVoxelCount, bbox });

    if (truncated || finalShapeRejected) {
      log.warn(
        'flood fill: rejected — region stopped by budget or shape gate; nothing committed',
        {
          maxVoxels: options.maxVoxels,
          voxelCountAtStop: filledVoxelCount,
          bbox,
          ijkStart,
        }
      );
      options.onRejected?.({ voxelCount: filledVoxelCount, bbox });
      return null;
    }

    if (filledVoxelCount === 0) {
      log.info(
        'flood fill: zero voxels (range may be too tight or seed isolated)',
        {
          ijkStart,
          toleranceMin: positiveMin,
          toleranceMax: positiveMax,
        }
      );
      return labelmap;
    }

    // A cancelled fill must not paint: bail before touching the labelmap.
    if (options.isCancelled?.() === true) {
      log.info('flood fill: cancellation requested; nothing committed', {
        ijkStart,
        voxelCountAtCancel: filledVoxelCount,
      });
      return null;
    }

    const { floodedPoints, voxelCount: committedVoxels } =
      commitSliceMasksToLabelmapVolume({
        labelmapVolume: labelmap,
        sliceMasks,
        width,
        height,
        paintIndex,
        historyVoxelManager: options.historyVoxelManager,
      });

    if (committedVoxels === 0 || floodedPoints.length === 0) {
      log.warn('flood fill: commit produced no labelmap voxels', {
        filledVoxelCount,
        committedVoxels,
        ijkStart,
      });
      return labelmap;
    }

    // Reported to `onCommitted` once cleanup is done — internal island removal
    // can add voxels beyond the raw flood, and callers (e.g. expand/shrink)
    // treat this set as "exactly what this run painted".
    let committedPoints = floodedPoints;

    log.info('flood fill: complete', {
      voxelCount: floodedPoints.length,
      ijkStart,
      paintIndex,
      usePreview,
    });

    const applyExternal = options.applyExternalIslandRemoval !== false;
    const applyInternal =
      options.applyInternalIslandRemoval !== false && applyExternal;

    if (!applyExternal && !applyInternal) {
      if (usePreview) {
        promotePreviewSegmentToFinal(
          labelmapReadVm,
          labelmapWriteVm,
          paintIndex,
          segmentIndex,
          floodedPoints,
          numPixelsPerSlice,
          width
        );
      }
      options.onCommitted?.(committedPoints);
      return labelmap;
    }
    const islandVerbose = options.islandRemovalVerboseLogging === true;

    const islandRemoval = new IslandRemoval({
      maxInternalRemove: options.maxInternalRemove ?? 128,
      fillInternalEdge: false,
      verboseLogging: islandVerbose,
    });

    const ijkPoints = [ijkStart];
    const initialized = islandRemoval.initialize(
      viewport,
      options.historyVoxelManager ?? labelmap.voxelManager,
      {
        points: ijkPoints,
        segmentIndex,
        previewSegmentIndex: usePreview ? paintIndex : segmentIndex,
      }
    );

    if (!initialized) {
      log.warn('island removal: initialize failed', { segmentIndex, ijkStart });
      if (usePreview) {
        promotePreviewSegmentToFinal(
          labelmapReadVm,
          labelmapWriteVm,
          paintIndex,
          segmentIndex,
          floodedPoints,
          numPixelsPerSlice,
          width
        );
      }
      options.onCommitted?.(committedPoints);
      return labelmap;
    }

    let islandFloodVoxels = 0;
    let externalClearedVoxels = 0;
    let internalSliceCount: number | undefined;

    if (applyExternal) {
      timeStart(FLOOD_FILL_ISLAND_EXTERNAL_TIMING_LABEL);
      islandFloodVoxels = islandRemoval.floodFillSegmentIsland();
      externalClearedVoxels = islandRemoval.removeExternalIslands();
      timeEnd(FLOOD_FILL_ISLAND_EXTERNAL_TIMING_LABEL);
      if (applyInternal) {
        timeStart(FLOOD_FILL_ISLAND_INTERNAL_TIMING_LABEL);
        const modifiedSlices = islandRemoval.removeInternalIslands();
        internalSliceCount = modifiedSlices?.length;
        timeEnd(FLOOD_FILL_ISLAND_INTERNAL_TIMING_LABEL);
      }
    }

    log.info('island removal: complete', {
      segmentIndex,
      applyExternalIslandRemoval: applyExternal,
      applyInternalIslandRemoval: applyInternal,
      floodedPointsBeforeIsland: floodedPoints.length,
      islandFloodVoxelsFromSegmentSet: islandFloodVoxels,
      externalIslandClearVoxels: externalClearedVoxels,
      internalRemovalModifiedSlices: internalSliceCount,
      islandRemovalVerboseLogging: islandVerbose,
    });

    // Internal island removal paints hole voxels beyond the raw flood. Track
    // them by the exact points island removal painted — a value-scan of the
    // modified slices would over-collect same-segment voxels from earlier
    // operations — so the reported set is complete in BOTH preview and
    // direct-paint modes.
    const internalFilledPoints = applyInternal
      ? islandRemoval.getInternalFilledPoints()
      : [];
    committedPoints =
      internalFilledPoints.length > 0
        ? floodedPoints.concat(internalFilledPoints)
        : floodedPoints;

    if (usePreview) {
      promotePreviewSegmentToFinal(
        labelmapReadVm,
        labelmapWriteVm,
        paintIndex,
        segmentIndex,
        committedPoints,
        numPixelsPerSlice,
        width
      );
    }

    options.onCommitted?.(committedPoints);

    // Debug visibility: verify final voxel values at flooded points before returning.
    if (ENABLE_VERBOSE_FLOOD_FILL_LOGS && floodedPoints.length > 0) {
      let finalSegmentCount = 0;
      let previewCount = 0;
      let zeroCount = 0;
      let otherCount = 0;
      for (let i = 0; i < floodedPoints.length; i++) {
        const [x, y, z] = floodedPoints[i];
        const index = z * numPixelsPerSlice + y * width + x;
        const value = Number(labelmap.voxelManager.getAtIndex(index) ?? 0);
        if (value === segmentIndex) {
          finalSegmentCount += 1;
        } else if (value === paintIndex) {
          previewCount += 1;
        } else if (value === 0) {
          zeroCount += 1;
        } else {
          otherCount += 1;
        }
      }
      log.info('[cornerstone-tools] flood fill final voxel check', {
        floodedPoints: floodedPoints.length,
        segmentIndex,
        paintIndex,
        usePreview,
        finalSegmentCount,
        previewCount,
        zeroCount,
        otherCount,
      });
    }

    return labelmap;
  } finally {
    timeEnd(FLOOD_FILL_RUN_TIMING_LABEL);
  }
}

export {
  runFloodFillSegmentation as default,
  runFloodFillSegmentation,
  getPositiveIntensityRange,
  getPositiveIntensityRangeRaw,
  getPositiveIntensityRangeVoiMapped,
  getDisplayVoiSnapshot,
};
export type { FloodFillSegmentationOptions };
